import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FeatureFlagKey } from "@steelbill/types";
import { api } from "@/lib/api";
import { endpoints } from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/**
 * The feature flags behind `settings.write` (spec §18.1, §18.4).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A STORED ROW BEATS THE CODE DEFAULT, AND THIS IS THE ONLY THING IN THE APP
 * THAT WRITES ONE.
 *
 * `FEATURE_FLAG_DEFAULTS` in `packages/types/src/flags.ts` governs a key that
 * has NEVER BEEN SEEDED and nothing else. Every deployed database has run
 * `02_seed.sql`, which writes a row per key and deliberately omits `enabled`
 * from its `ON DUPLICATE KEY UPDATE` list — so `enabled` is operator state, and
 * editing `flags.ts` changes nothing at all on a live box. That cost an evening
 * on 2026-08-16: `challans` was flipped to `enabled: true` in code, deployed,
 * and the app went on saying "Delivery challans are not switched on" because the
 * row still said 0. The fix was an `UPDATE`. This module is the replacement for
 * that `UPDATE`.
 *
 * So there is no local optimism anywhere below, and no client-side notion of
 * what a flag "should" be. The screen renders what the server says the ROW says,
 * the mutation writes the ROW, and the answer is re-read.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * NOTHING HERE RESOLVES A FLAG FOR THE REST OF THE APP, and it must not grow
 * into that. `GET /flags` needs `settings.write`, which the OWNER does not hold,
 * so a `useFlag()` built on this would answer 403 for the person it was for. A
 * flagged module is discovered the way every other one discovers it: the API
 * answers 404 and the screen renders `FeatureOff` (§18.2).
 */

/* -------------------------------------------------------------------------- */
/* The response                                                                */
/* -------------------------------------------------------------------------- */

/**
 * One flag as the ADMIN screen needs it.
 *
 * TRANSCRIBED from `FeatureFlagView` in `apps/api/src/services/feature-flags.ts`
 * — the API is the authority and this is a copy of its shape, exactly as
 * `lib/endpoints.ts` prescribes for a response the SPA reads. It is not an
 * independent definition and must not acquire fields the server does not send.
 */
export interface FeatureFlagRow {
  /** The typed key — `credit_notes`. Shown as the technical name. */
  key: string;
  enabled: boolean;
  /** The §18.5 "Gates" column, in English, straight off the row. */
  description: string;
  /** §18.4 — who is answerable. `null` only while no row exists yet. */
  owner: string | null;
  /** `YYYY-MM-DD`, or `null` for a permanent switch or an unwritten row. */
  reviewBy: string | null;
  isPermanent: boolean;
  /** §18.4's "stale — remove or extend", computed by the server in IST. */
  isStale: boolean;
  /**
   * FALSE MEANS NO ROW HAS EVER BEEN WRITTEN and what is shown is the §18.5
   * default. The first toggle of such a key is a CREATE, and §18.4 makes it
   * carry an owner and (unless permanent) a review date — which is why the
   * screen asks for both in that one case and the server 400s without them.
   */
  isSeeded: boolean;
  /**
   * FALSE MEANS A LEFTOVER ROW whose key the code no longer has (§18.4). It
   * gates nothing; it is surfaced so the leftover is visible rather than
   * invisible, and the honest action on it is a `DELETE`, which this API
   * deliberately does not offer.
   */
  isKnown: boolean;
}

/**
 * `GET /flags` — every flag, plus the two §18.4 counts.
 *
 * The counts are COMPUTED BY THE SERVER over the same list it just sent, and the
 * screen shows them rather than re-folding the array. Re-deriving them here
 * would be a second opinion that goes wrong the moment the server's definition
 * of "live" changes — and "live" already means something specific: enabled AND
 * not permanent, because a permanent kill switch is not one of the temporary
 * things the cap is counting.
 */
export interface FeatureFlagListResponse {
  flags: FeatureFlagRow[];
  liveNonPermanent: number;
  stale: number;
}

/** `PUT /flags/:key` → the row as it now stands. */
export interface FeatureFlagMutationResponse {
  flag: FeatureFlagRow;
}

/* -------------------------------------------------------------------------- */
/* Reading                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Every flag, in §18.5 table order — the server's order, never re-sorted.
 *
 * NOT SORTED BY STATE, AND NOT BY STALENESS. The temptation is to float the
 * stale ones or group the ons together; both would move `invoices` around the
 * list depending on facts that change, and the one thing an operator needs from
 * a list of switches is that a switch stays where it was last time. The §18.5
 * order is also the order the roadmap is written in, so the reader is walking
 * the same list twice rather than two orderings of one set.
 *
 * `refetchOnWindowFocus` IS LEFT AT THE APP DEFAULT deliberately. Two API
 * processes can be serving this box and the flag cache has a 30-second TTL, so
 * the honest thing is to re-read rather than to keep painting a snapshot from
 * whenever the tab was opened.
 */
export function useFeatureFlags() {
  return useQuery({
    queryKey: queryKeys.flags.list(),
    queryFn: async ({ signal }) =>
      api.get<FeatureFlagListResponse>(endpoints.flags, { signal }),
  });
}

/* -------------------------------------------------------------------------- */
/* Writing                                                                     */
/* -------------------------------------------------------------------------- */

/** Exactly the body `FlagToggleSchema` accepts — it is `.strict()`. */
export interface FeatureFlagToggleBody {
  enabled: boolean;
  /** §18.4 — REQUIRED on a first toggle (the create). Ignored-if-absent after. */
  owner?: string;
  /** §18.4 — `YYYY-MM-DD`. Required on a first toggle of a non-permanent key. */
  reviewBy?: string;
}

/**
 * `PUT /flags/:key` — write the row, and let the server audit it.
 *
 * THE AUDIT ROW IS THE SERVER'S AND SHARES THE WRITE'S TRANSACTION (§13):
 * `setFeatureFlag` records `feature_flag.update` with the before- and
 * after-images inside the same `$transaction`, so a rolled-back toggle leaves no
 * claim that it happened. There is deliberately nothing here that "reports" a
 * flip — a client-side audit call is a second write that can fail on its own and
 * would produce a trail with holes in exactly the rows somebody is disputing.
 *
 * NO OPTIMISTIC UPDATE, ON PURPOSE. Three of the server's answers are refusals
 * this client cannot predict — a first toggle with no owner (400), the same with
 * no review date (400), and a key the build no longer knows (400 from the enum)
 * — and an optimistic switch would slide across, then slide back, which reads as
 * the toggle having worked and then been undone by somebody else. The switch
 * moves when the row moves.
 *
 * THE ANSWER IS WRITTEN STRAIGHT INTO THE CACHE and then the list is
 * invalidated. The write is what makes the row correct instantly for the reader
 * who just acted; the invalidation is what refreshes `liveNonPermanent` and
 * `stale`, which are folds over the WHOLE list and which this one row cannot
 * update on its own.
 */
export function useSetFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, body }: { key: FeatureFlagKey; body: FeatureFlagToggleBody }) =>
      api.put<FeatureFlagMutationResponse>(endpoints.flag(key), body),
    onSuccess: (result) => {
      queryClient.setQueryData<FeatureFlagListResponse>(queryKeys.flags.list(), (current) =>
        current === undefined
          ? current
          : {
              ...current,
              flags: current.flags.map((flag) =>
                flag.key === result.flag.key ? result.flag : flag,
              ),
            },
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.flags.all });
    },
  });
}
